import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import VideoInput from "@/components/admin/VideoInput";
import BulkUploadDialog from "@/components/admin/BulkUploadDialog";

const EMPTY = { title: "", date: "", preacher: "", theme: "", description: "", video_type: "youtube", video_url: "", thumbnail_url: "", duration: "" };

export default function AdminCultes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/cultes").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const upd = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/cultes/${editing.id}`, form);
        toast.success("Culte mis à jour");
      } else {
        await api.post("/cultes", form);
        toast.success("Culte ajouté");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce culte ?")) return;
    await api.delete(`/cultes/${id}`);
    toast.success("Culte supprimé");
    load();
  };

  return (
    <div data-testid="admin-cultes">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-gold-dark text-xs tracking-[0.3em] uppercase">Médiathèque</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-deepblue-900 mt-1">Cultes passés</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setBulkOpen(true)} variant="outline" data-testid="bulk-upload-open-btn">
            <UploadCloud className="w-4 h-4 mr-1" /> Téléverser plusieurs
          </Button>
          <Button onClick={openCreate} className="bg-gold hover:bg-gold-hover text-white rounded-full" data-testid="add-culte-btn">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Titre</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Prédicateur</TableHead>
              <TableHead>Thème</TableHead>
              <TableHead>Vues</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Aucun culte</TableCell></TableRow>
            )}
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`culte-row-${c.id}`}>
                <TableCell className="font-medium text-deepblue-900">{c.title}</TableCell>
                <TableCell>{new Date(c.date).toLocaleDateString("fr-FR")}</TableCell>
                <TableCell>{c.preacher}</TableCell>
                <TableCell>{c.theme || "—"}</TableCell>
                <TableCell>{c.views || 0}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-culte-${c.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`delete-culte-${c.id}`}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le culte" : "Ajouter un culte"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Titre" required><Input value={form.title} onChange={upd("title")} data-testid="form-title" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date" required><Input type="date" value={form.date} onChange={upd("date")} data-testid="form-date" /></Field>
              <Field label="Prédicateur" required><Input value={form.preacher} onChange={upd("preacher")} data-testid="form-preacher" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Thème"><Input value={form.theme} onChange={upd("theme")} /></Field>
              <Field label="Durée"><Input value={form.duration} onChange={upd("duration")} placeholder="42 min" /></Field>
            </div>
            <Field label="Description"><Textarea value={form.description} onChange={upd("description")} rows={3} /></Field>

            <div className="pt-2">
              <Label className="text-base font-serif text-deepblue-900">Vidéo du culte</Label>
              <div className="mt-2">
                <VideoInput
                  value={{ video_type: form.video_type, video_url: form.video_url }}
                  onChange={(next) => setForm((f) => ({ ...f, ...next }))}
                />
              </div>
            </div>

            <Field label="Miniature (URL image, optionnel)">
              <Input value={form.thumbnail_url} onChange={upd("thumbnail_url")} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-gold hover:bg-gold-hover text-white" data-testid="save-culte-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkUploadDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onDone={load}
      />
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
